import {
  getProfile,
  listGrants,
  Profile,
  smsOptOut,
  sendCodeToPhoneNumber,
  SendPhoneVerificationCodePayload,
  updateProfile,
  verifyPhoneNumberCode,
  VerifyVerificationCodePayload,
  getSSHKeys,
  createSSHKey,
  SSHKey,
  deleteSSHKey,
  updateSSHKey,
  getTrustedDevices,
  TrustedDevice,
  deleteTrustedDevice,
  disableTwoFactor,
} from '@bitnimbus/api-v4/lib/profile';
import {
  QueryClient,
  useMutation,
  useQuery,
  useQueryClient,
} from 'react-query';
import {
  APIError,
  Filter,
  Params,
  ResourcePage,
  Response,
} from '@bitnimbus/api-v4/lib/types';
import { Grants } from '../../../api-v4/lib';
import { queryPresets } from './base';
import { GetAccountInfoKey as accountQueryKey } from './account';
import { EventWithStore } from 'src/events';

export const queryKey = 'profile';
export const GetProfileKey = ['profile'];

export const useProfile = (givenProfile?: Profile) => {
  const result = useQuery<Response<Profile>, APIError[]>(queryKey, getProfile, {
    ...queryPresets.oneTimeFetch,
    initialData: givenProfile,
  });
  return {
    ...result,
    data: result?.data?.data,
  };
};

export const useMutateProfile = () => {
  const queryClient = useQueryClient();
  return useMutation<Profile, APIError[], Partial<Profile>>(
    (data) => updateProfile(data),
    { onSuccess: (newData) => updateProfileData(newData, queryClient) }
  );
};

export const updateProfileData = (
  newData: Partial<Profile>,
  queryClient: QueryClient
): void => {
  queryClient.setQueryData(queryKey, (oldData: Profile) => ({
    ...oldData,
    ...newData,
  }));
};

export const useGrants = () => {
  const { data: profile } = useProfile();
  return useQuery<Grants, APIError[]>(
    [queryKey, 'grants'],
    () => listGrants(profile?.username),
    {
      ...queryPresets.oneTimeFetch,
      enabled: Boolean(profile?.restricted),
    }
  );
};

export const getProfileData = (queryClient: QueryClient) =>
  queryClient.getQueryData<Profile>(queryKey);
export const getGrantData = (queryClient: QueryClient) =>
  queryClient.getQueryData<Grants>([queryKey, 'grants']);

export const useSMSOptOutMutation = () => {
  const queryClient = useQueryClient();
  return useMutation<{}, APIError[]>(smsOptOut, {
    onSuccess: () => {
      updateProfileData({ phone: null }, queryClient);
    },
  });
};

export const useSendPhoneVerificationCodeMutation = () =>
  useMutation<null, APIError[], SendPhoneVerificationCodePayload>(
    async (payload) => {
      const response = await sendCodeToPhoneNumber(payload);
      return response.data || null;
    }
  );

export const useVerifyPhoneVerificationCodeMutation = () =>
  useMutation<{}, APIError[], VerifyVerificationCodePayload>(
    async (payload) => {
      const response = await verifyPhoneNumberCode(payload);
      return response.data || {};
    }
  );

export const useSSHKeysQuery = (
  params?: Params,
  filter?: Filter,
  enabled = true
) =>
  useQuery(
    [queryKey, 'ssh-keys', 'paginated', params, filter],
    () => getSSHKeys(params, filter),
    {
      keepPreviousData: true,
      enabled,
    }
  );

export const useCreateSSHKeyMutation = () => {
  const queryClient = useQueryClient();
  return useMutation<SSHKey, APIError[], { label: string; ssh_key: string }>(
    createSSHKey,
    {
      onSuccess() {
        queryClient.invalidateQueries([queryKey, 'ssh-keys']);
        // also invalidate the /account/users data because that endpoint returns some SSH key data
        queryClient.invalidateQueries([accountQueryKey, 'users']);
      },
    }
  );
};

export const useUpdateSSHKeyMutation = (id: number) => {
  const queryClient = useQueryClient();
  return useMutation<SSHKey, APIError[], { label: string }>(
    (data) => updateSSHKey(id, data),
    {
      onSuccess() {
        queryClient.invalidateQueries([queryKey, 'ssh-keys']);
        // also invalidate the /account/users data because that endpoint returns some SSH key data
        queryClient.invalidateQueries([accountQueryKey, 'users']);
      },
    }
  );
};

export const useDeleteSSHKeyMutation = (id: number) => {
  const queryClient = useQueryClient();
  return useMutation<{}, APIError[]>(() => deleteSSHKey(id), {
    onSuccess() {
      queryClient.invalidateQueries([queryKey, 'ssh-keys']);
      // also invalidate the /account/users data because that endpoint returns some SSH key data
      queryClient.invalidateQueries([accountQueryKey, 'users']);
    },
  });
};

export const sshKeyEventHandler = (event: EventWithStore) => {
  // This event handler is a bit agressive and will over-fetch, but UX will
  // be great because this will ensure Cloud has up to date data all the time.

  event.queryClient.invalidateQueries([queryKey, 'ssh-keys']);
  // also invalidate the /account/users data because that endpoint returns some SSH key data
  event.queryClient.invalidateQueries([accountQueryKey, 'users']);
};

export const useTrustedDevicesQuery = (params?: Params, filter?: Filter) =>
  useQuery<ResourcePage<TrustedDevice>, APIError[]>(
    [queryKey, 'trusted-devices', 'paginated', params, filter],
    () => getTrustedDevices(params, filter),
    {
      keepPreviousData: true,
    }
  );

export const useRevokeTrustedDeviceMutation = (id: number) => {
  const queryClient = useQueryClient();
  return useMutation<{}, APIError[]>(() => deleteTrustedDevice(id), {
    onSuccess() {
      queryClient.invalidateQueries([queryKey, 'trusted-devices']);
    },
  });
};

export const useDisableTwoFactorMutation = () => {
  const queryClient = useQueryClient();
  return useMutation<{}, APIError[]>(disableTwoFactor, {
    onSuccess() {
      queryClient.invalidateQueries([queryKey]);
    },
  });
};
